import React, { useState, createContext, useContext } from 'react';
export type Language = 'en' | 'fr';
type TranslationKey = string;
type Translations = Record<Language, Record<TranslationKey, string>>;
const translations: Translations = {
  en: {
    // Welcome
    'welcome.headline': 'How can we help?',
    'welcome.find': 'Find Product Location',
    'welcome.list': 'Build a Shopping List',
    'welcome.diy': 'DIY Project Instructions',
    'welcome.contractor': 'Trusted Contractor Quotes',
    'welcome.deals': "Today's DEALS",
    'welcome.orDescribe': 'Or describe what you need help with...',
    'welcome.talkToAI': 'Talk to AI Shopping Assistant',
    'welcome.fastestHelp': 'Fastest Help',
    'welcome.trustBadge': 'Trusted. Secure. Private.',
    'welcome.tapToSpeak': 'Tap to speak',
    'welcome.listening': 'Listening...',
    'welcome.aiGreeting': "Tell me about your project and I'll build you a shopping list of everything you need.",
    'welcome.aiGreetingContractor': "Tell me about your project, and I'll connect you with one of our trusted contractors.",
    'welcome.aiConfirmContractor': "Great. I'm reaching out to a few trusted contractors in your area — they'll be in touch shortly with quotes.",
    'welcome.listTitle': 'Your Shopping List',
    'welcome.listSubtitle': "Here's what you'll need.",
    'welcome.projectSummary': 'Your project',
    'welcome.tapToAddMore': 'Tap to add more',
    'welcome.dealsTitle': "Today's Deals",
    'welcome.dealsSubtitle': 'Browse by category',
    'deals.tools': 'Tools',
    'deals.paint': 'Paint',
    'deals.lumber': 'Lumber',
    'deals.electrical': 'Electrical',
    'deals.plumbing': 'Plumbing',
    'deals.garden': 'Garden',
    'deals.dealsCount': 'deals',
    'deals.off': 'OFF',
    'deals.msrp': 'MSRP',
    'deals.productsTitle': 'Deals',
    'welcome.requestAssociate': 'Request Human Associate',
    'associate.requesting': 'Requesting...',
    'associate.onTheWay': 'Associate on the way',
    // Chat
    'chat.greeting.shoppingList': "Tell me about your project and I'll build you a shopping list of everything you need.",
    'chat.greeting.diy': "Tell me about your project and I'll provide instructions on how to do it yourself.",
    'chat.greeting.contractor': 'Tell me about the project you want the contractor to quote.',
    'chat.greeting.general': 'I can answer any questions you have about your project or product here in the store.',
    'chat.greeting.default': 'Great. Will the deck be attached to your home?',
    'chat.tapToSpeak': 'Tap to speak',
    'chat.listening': 'Listening...',
    'chat.header.shoppingList': 'Shopping List Assistant',
    'chat.header.diy': 'DIY Instructions',
    'chat.header.contractor': 'Contractor Quote',
    'chat.header.general': 'AI Shopping Assistant',
    'chat.header.default': 'Project Setup',
    'chat.step.describe': 'Describe your project',
    // Shopping List
    'list.title': 'My Shopping List',
    'list.subtitle': "Type a product or tap the mic — we'll tell you the aisle.",
    'list.searchPlaceholder': 'What are you looking for?',
    'list.searchHint': 'Try: "hammer", "interior paint", "deck screws"',
    'list.sectionTitle': 'Shopping List',
    'list.empty': 'Your list is empty',
    'list.emptyHint': 'Search for a product above to get started.',
    'list.aiAssistant': 'AI Shopping Assistant',
    'list.requestAssociate': 'Request Human Associate',
    'list.associateConfirm': "We've notified the nearest associate.",
    'list.associateWait': 'Estimated wait: 2 minutes',
    // Header
    'header.poweredBy': 'Powered by'
  },
  fr: {
    // Welcome
    'welcome.headline': 'Comment pouvons-nous vous aider?',
    'welcome.find': 'Trouver un produit en magasin',
    'welcome.list': "Créer une liste d'achats",
    'welcome.diy': 'Instructions pour projet bricolage',
    'welcome.contractor': "Soumissions d'entrepreneurs de confiance",
    'welcome.deals': 'OFFRES du jour',
    'welcome.orDescribe': 'Ou décrivez ce dont vous avez besoin...',
    'welcome.talkToAI': "Parler à l'assistant d'achats IA",
    'welcome.fastestHelp': 'Aide la plus rapide',
    'welcome.trustBadge': 'Fiable. Sécurisé. Privé.',
    'welcome.tapToSpeak': 'Touchez pour parler',
    'welcome.listening': "À l'écoute...",
    'welcome.aiGreeting': "Parlez-moi de votre projet et je vais créer une liste d'achats avec tout ce dont vous avez besoin.",
    'welcome.aiGreetingContractor': "Parlez-moi de votre projet et je vous mettrai en contact avec l'un de nos entrepreneurs de confiance.",
    'welcome.aiConfirmContractor': 'Parfait. Je contacte quelques entrepreneurs de confiance dans votre région — ils vous enverront des soumissions sous peu.',
    'welcome.listTitle': "Votre liste d'achats",
    'welcome.listSubtitle': 'Voici ce dont vous aurez besoin.',
    'welcome.projectSummary': 'Votre projet',
    'welcome.tapToAddMore': 'Touchez pour ajouter',
    'welcome.dealsTitle': 'Offres du jour',
    'welcome.dealsSubtitle': 'Parcourir par catégorie',
    'deals.tools': 'Outils',
    'deals.paint': 'Peinture',
    'deals.lumber': 'Bois',
    'deals.electrical': 'Électrique',
    'deals.plumbing': 'Plomberie',
    'deals.garden': 'Jardin',
    'deals.dealsCount': 'offres',
    'deals.off': 'RABAIS',
    'deals.msrp': 'PDSF',
    'deals.productsTitle': 'Offres',
    'welcome.requestAssociate': 'Demander un associé',
    'associate.requesting': 'Demande en cours...',
    'associate.onTheWay': 'Associé en route',
    // Chat
    'chat.greeting.shoppingList': "Parlez-moi de votre projet et je vais créer une liste d'achats avec tout ce dont vous avez besoin.",
    'chat.greeting.diy': 'Parlez-moi de votre projet et je vais vous fournir des instructions pour le réaliser vous-même.',
    'chat.greeting.contractor': 'Parlez-moi du projet que vous souhaitez faire évaluer par un entrepreneur.',
    'chat.greeting.general': 'Je peux répondre à toutes vos questions sur votre projet ou un produit ici en magasin.',
    'chat.greeting.default': 'Parfait. La terrasse sera-t-elle attachée à votre maison?',
    'chat.tapToSpeak': 'Touchez pour parler',
    'chat.listening': "À l'écoute...",
    'chat.header.shoppingList': "Assistant liste d'achats",
    'chat.header.diy': 'Instructions bricolage',
    'chat.header.contractor': "Évaluation d'entrepreneur",
    'chat.header.general': "Assistant d'achats IA",
    'chat.header.default': 'Configuration du projet',
    'chat.step.describe': 'Décrivez votre projet',
    // Shopping List
    'list.title': "Ma liste d'achats",
    'list.subtitle': "Tapez un produit ou touchez le micro — on vous dira l'allée.",
    'list.searchPlaceholder': 'Que cherchez-vous?',
    'list.searchHint': 'Essayez: "marteau", "peinture", "vis à terrasse"',
    'list.sectionTitle': "Liste d'achats",
    'list.empty': 'Votre liste est vide',
    'list.emptyHint': 'Cherchez un produit ci-dessus pour commencer.',
    'list.aiAssistant': "Assistant d'achats IA",
    'list.requestAssociate': 'Demander un associé',
    'list.associateConfirm': "Nous avons prévenu l'associé le plus proche.",
    'list.associateWait': "Temps d'attente estimé: 2 minutes",
    // Header
    'header.poweredBy': 'Propulsé par'
  }
};
type LanguageContextValue = {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: TranslationKey) => string;
};
const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [lang, setLang] = useState<Language>('en');
  const t = (key: TranslationKey) => translations[lang][key] ?? translations.en[key] ?? key;
  return <LanguageContext.Provider value={{
    lang,
    setLang,
    t
  }} data-id="element-71">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
}